import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { Config } from '../config';
import SignaturePad from './SignaturePad';

interface EstimateSignatureSectionProps {
  estimateId: string;
  customerName: string;
  onSigned: (signerName: string, signedAt: string) => void;
}

const CONSENT_TEXT = 'By signing this estimate, I acknowledge that I have reviewed and agree to the terms, pricing, and scope of work described in this document. I understand that this electronic signature is legally binding under the ESIGN Act and UETA.';
const ERROR_FILL_ALL_FIELDS = 'Please fill in your full name, provide a signature, and agree to the consent.';
const SUBMIT_BUTTON_TEXT = 'Sign Estimate';
const SUBMITTING_TEXT = 'Signing...';

const EstimateSignatureSection: React.FC<EstimateSignatureSectionProps> = ({
  estimateId,
  customerName,
  onSigned
}) => {
  const [signerFullName, setSignerFullName] = useState(customerName || '');
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [inputType, setInputType] = useState<'draw' | 'type'>('draw');
  const [consentGiven, setConsentGiven] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const API_BASE_URL = Config.Env.ApiBaseUrl;

  const handleSignatureChange = (dataUrl: string | null, type: 'draw' | 'type') => {
    setSignatureDataUrl(dataUrl);
    setInputType(type);
  };

  const handleSubmit = async () => {
    setError(null);

    if (!signerFullName.trim() || !signatureDataUrl || !consentGiven) {
      setError(ERROR_FILL_ALL_FIELDS);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/estimates/${estimateId}/sign`,
        {
          signerFullName: signerFullName.trim(),
          signatureImageData: signatureDataUrl,
          consentGiven,
          inputType
        }
      );

      if (response.data.status === 'success') {
        setSuccess(true);
        onSigned(
          response.data.data.signerName,
          response.data.data.signedAt
        );
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to sign estimate. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <Card className="shadow mb-4 border-success">
        <Card.Body className="p-4 text-center">
          <div className="text-success mb-3">
            <i className="fas fa-check-circle fa-3x"></i>
          </div>
          <h4 className="text-success">Estimate Signed Successfully</h4>
          <p className="text-muted mb-0">
            A signed copy has been sent to your email address for your records.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow mb-4">
      <Card.Header className="bg-primary bg-opacity-10 py-3">
        <h4 className="m-0">
          <i className="fas fa-file-signature me-2"></i>
          Sign Estimate
        </h4>
      </Card.Header>
      <Card.Body className="p-4">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}

        <div className="alert alert-info mb-4">
          <i className="fas fa-info-circle me-2"></i>
          Please review the estimate above, then sign below to confirm your agreement.
        </div>

        {/* Full Name Input */}
        <Form.Group className="mb-4">
          <Form.Label><strong>Full Legal Name</strong></Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter your full legal name"
            value={signerFullName}
            onChange={(e) => setSignerFullName(e.target.value)}
            disabled={isSubmitting}
            className="py-2"
          />
        </Form.Group>

        {/* Signature Pad */}
        <Form.Group className="mb-4">
          <Form.Label><strong>Signature</strong></Form.Label>
          <SignaturePad onSignatureChange={handleSignatureChange} />
        </Form.Group>

        {/* Consent Checkbox */}
        <Form.Group className="mb-4">
          <div className="p-3 bg-light rounded border">
            <Form.Check
              type="checkbox"
              id="consent-checkbox"
              label={
                <span className="small">
                  {CONSENT_TEXT}
                </span>
              }
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              disabled={isSubmitting}
            />
          </div>
        </Form.Group>

        {/* Submit Button */}
        <div className="d-grid">
          <Button
            className="modern-cta-button py-3"
            onClick={handleSubmit}
            disabled={isSubmitting || !signerFullName.trim() || !signatureDataUrl || !consentGiven}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {SUBMITTING_TEXT}
              </>
            ) : (
              <>
                <i className="fas fa-file-signature me-2"></i>
                {SUBMIT_BUTTON_TEXT}
              </>
            )}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EstimateSignatureSection;
